import { useState, useRef, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { format, parse, isValid } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

interface DateCellProps {
  value: Date | string | null;
  onChange: (value: Date | null) => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
  dateFormat?: string;
  includeTime?: boolean;
  className?: string;
}

export function DateCell({
  value,
  onChange,
  isEditing,
  onStartEdit,
  onEndEdit,
  dateFormat = 'MM/dd/yyyy',
  includeTime = false,
  className,
}: DateCellProps) {
  const [editValue, setEditValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const actualFormat = includeTime ? `${dateFormat} HH:mm` : dateFormat;

  const parseDate = useCallback(
    (dateValue: Date | string | null): Date | null => {
      if (!dateValue) return null;
      if (dateValue instanceof Date) return dateValue;
      const parsed = new Date(dateValue);
      return isValid(parsed) ? parsed : null;
    },
    []
  );

  const dateObj = parseDate(value);

  const wasEditingRef = useRef(false);
  useEffect(() => {
    if (isEditing && !wasEditingRef.current) {
      // Just entered edit mode — initialize
      setEditValue(dateObj ? format(dateObj, actualFormat) : '');
      setViewMonth(null);
      setIsOpen(true);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    } else if (!isEditing && wasEditingRef.current) {
      // Just exited edit mode
      setIsOpen(false);
    }
    wasEditingRef.current = isEditing;
  }, [isEditing, dateObj, actualFormat]);

  const handleInputChange = useCallback(
    (str: string) => {
      setEditValue(str);

      // Try to parse the date
      const parsed = parse(str, actualFormat, new Date());
      if (isValid(parsed)) {
        onChange(parsed);
      }
    },
    [actualFormat, onChange]
  );

  const handleSave = useCallback(() => {
    const parsed = parse(editValue, actualFormat, new Date());
    if (isValid(parsed)) {
      if (!dateObj || parsed.getTime() !== dateObj.getTime()) {
        flushSync(() => { onChange(parsed); });
      }
    } else if (editValue === '') {
      if (dateObj !== null) {
        flushSync(() => { onChange(null); });
      }
    }
    onEndEdit();
  }, [editValue, actualFormat, dateObj, onChange, onEndEdit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setEditValue(dateObj ? format(dateObj, actualFormat) : '');
        onEndEdit();
      } else if (e.key === 'Tab') {
        handleSave();
      }
    },
    [handleSave, onEndEdit, dateObj, actualFormat]
  );

  const handleDateSelect = useCallback(
    (date: Date) => {
      // Use flushSync to ensure onChange processes completely
      // before React batches the state updates from closing the popover
      flushSync(() => {
        onChange(date);
      });
      setEditValue(format(date, actualFormat));
      setIsOpen(false);
      onEndEdit();
    },
    [actualFormat, onChange, onEndEdit]
  );

  // Generate simple calendar
  const renderCalendar = () => {
    const today = new Date();
    const displayMonth = viewMonth || dateObj || today;
    const year = displayMonth.getFullYear();
    const month = displayMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();

    const days: (Date | null)[] = [];
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    return (
      <div className="p-2">
        <div className="flex justify-between items-center mb-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setViewMonth(new Date(year, month - 1, 1));
            }}
          >
            &lt;
          </Button>
          <span className="text-sm font-medium">
            {format(displayMonth, 'MMMM yyyy')}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setViewMonth(new Date(year, month + 1, 1));
            }}
          >
            &gt;
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
            <div key={d} className="p-1 text-gray-500 font-medium">
              {d}
            </div>
          ))}
          {days.map((day, i) => (
            <div
              key={i}
              className={cn(
                'p-1 cursor-pointer rounded hover:bg-gray-100',
                day && dateObj && day.toDateString() === dateObj.toDateString()
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : '',
                day && day.toDateString() === today.toDateString()
                  ? 'border border-blue-500'
                  : ''
              )}
              onClick={(e) => { e.stopPropagation(); if (day) handleDateSelect(day); }}
            >
              {day?.getDate() ?? ''}
            </div>
          ))}
        </div>
        <div className="mt-2 flex justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); handleDateSelect(today); }}
          >
            Today
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              flushSync(() => { onChange(null); });
              setIsOpen(false);
              onEndEdit();
            }}
          >
            Clear
          </Button>
        </div>
      </div>
    );
  };

  if (isEditing) {
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div className="relative w-full h-full">
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => handleInputChange(e.target.value)}
              onBlur={(e) => {
                // Don't save/close if clicking inside the popover calendar
                const relatedTarget = e.relatedTarget as HTMLElement | null;
                const popoverContent = document.querySelector('[data-radix-popper-content-wrapper]');
                if (popoverContent?.contains(relatedTarget)) return;
                // Also check if the click target is inside a popover
                if (relatedTarget?.closest('[data-radix-popper-content-wrapper]')) return;
                // Delay to allow popover click to fire first
                setTimeout(() => {
                  if (isOpen) return; // Popover still open means user clicked inside it
                  handleSave();
                }, 100);
              }}
              onKeyDown={handleKeyDown}
              placeholder={actualFormat.toLowerCase()}
              className={cn(
                'w-full h-full px-2 py-1 pr-8 text-sm bg-white border-2 border-blue-500 outline-none',
                'rounded-none focus:ring-0',
                className
              )}
            />
            <CalendarIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          {renderCalendar()}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <div
      className={cn(
        'w-full h-full px-2 py-1 text-sm truncate cursor-text flex items-center',
        !dateObj && 'text-gray-400',
        className
      )}
      onDoubleClick={onStartEdit}
    >
      {dateObj ? format(dateObj, actualFormat) : ''}
    </div>
  );
}

export default DateCell;
